/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { tv } from 'tailwind-variants';
import { getTestProps } from 'lib/testing/utils';
import { RemixIcon } from 'helpers/RemixIcon';
import { useRouter } from 'next/navigation';
import { Context, getWidgetData, SearchWidgetItem, WidgetRequestData } from '@sitecore-cloudsdk/search/browser';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import { SEARCH_CONSTANTS } from 'helpers/Constants/SiteConfig';
import { sendGTMEvent } from '@next/third-parties/google';
const GlobalSearchInput = ({ value = '', placeholder = 'Search Keywords', onSearch, onClear, disabled = false, maxLength, onSuggestionSelect, showSuggestions = true, minCharactersForSuggestions = 3, id, suggestionName = 'title_auto_complete', searchType })=>{
    const router = useRouter();
    const { isSearchInitialized } = useSearchInitialization();
    const [searchValue, setSearchValue] = useState(value);
    const [suggestions, setSuggestions] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const clearButtonRef = useRef(null);
    const isClearingRef = useRef(false);
    const removeSpecialCharacters = (value)=>{
        return value.replace(/[^a-zA-Z0-9- ]/g, '');
    };
    useEffect(()=>{
        setSearchValue(value);
    }, [
        value
    ]);
    // Update dropdown visibility based on input and suggestions
    useEffect(()=>{
        const shouldShow = showSuggestions && isFocused && searchValue && searchValue.length >= minCharactersForSuggestions && suggestions.length > 0 ? true : false;
        setShowDropdown(shouldShow);
        setSelectedSuggestionIndex(-1);
    }, [
        searchValue,
        isFocused,
        suggestions,
        showSuggestions,
        minCharactersForSuggestions
    ]);
    const handleInputChange = (e)=>{
        const newValue = removeSpecialCharacters(e.target.value);
        setSearchValue(newValue);
    };
    const handleClear = ()=>{
        isClearingRef.current = true;
        setSearchValue('');
        onClear?.();
        // Use setTimeout to ensure focus happens after any blur events
        setTimeout(()=>{
            inputRef.current?.focus();
            setIsFocused(true);
            // Reset the clearing flag after a short delay
            setTimeout(()=>{
                isClearingRef.current = false;
            }, 200);
        }, 0);
    };
    /** Push search_click at global-search level; flag SearchResult to skip a duplicate. */ const pushSearchClickEvent = ({ searchTerm, searchInput, suggestionPosition, results = '' })=>{
        sendGTMEvent({
            event: 'search_click',
            search_term: searchTerm,
            search_type: searchType || 'global',
            search_input: searchInput,
            suggestion_position: typeof suggestionPosition === 'number' ? suggestionPosition : '',
            results
        });
        if ("object" !== 'undefined') {
            sessionStorage.setItem('search_result_searchClickSent', searchTerm);
            if (searchType) {
                sessionStorage.setItem('search_result_searchType', searchType);
            }
        }
    };
    const handleSearch = ()=>{
        if (searchValue.trim()) {
            const term = searchValue.trim();
            // Call the onSearch callback if provided
            onSearch?.(term);
            pushSearchClickEvent({
                searchTerm: term,
                searchInput: term
            });
            // Redirect to /search with search term as query parameter
            const qs = new URLSearchParams({
                searchterm: term
            }).toString();
            router.push(`${SEARCH_CONSTANTS.pageUrl}?${qs}`);
        }
    };
    const handleKeyDown = (e)=>{
        switch(e.key){
            case 'Enter':
                e.preventDefault();
                if (showDropdown && selectedSuggestionIndex >= 0) {
                    // Select the highlighted suggestion
                    const selectedSuggestion = suggestions[selectedSuggestionIndex];
                    const typedInput = searchValue.trim();
                    const suggestionPosition = selectedSuggestionIndex + 1;
                    if (selectedSuggestion?.url && "object" !== 'undefined') {
                        // Navigate to the suggestion's URL
                        try {
                            pushSearchClickEvent({
                                searchTerm: selectedSuggestion.name || typedInput,
                                searchInput: typedInput,
                                suggestionPosition,
                                results: 'true'
                            });
                            window.location.href = selectedSuggestion.url;
                        } catch (error) {
                            console.error('Error navigating to suggestion URL:', error);
                            // Fallback to router navigation if window.location fails
                            if (router) {
                                setSearchValue(selectedSuggestion.name);
                                onSuggestionSelect?.(selectedSuggestion);
                                setShowDropdown(false);
                                setSelectedSuggestionIndex(-1);
                                pushSearchClickEvent({
                                    searchTerm: selectedSuggestion.name || typedInput,
                                    searchInput: typedInput,
                                    suggestionPosition
                                });
                                const qs = new URLSearchParams({
                                    searchterm: selectedSuggestion.name
                                }).toString();
                                router.push(`${SEARCH_CONSTANTS.pageUrl}?${qs}`);
                            }
                        }
                    } else {
                        // Fallback: redirect to search page with the selected suggestion
                        setSearchValue(selectedSuggestion?.name || '');
                        onSuggestionSelect?.(selectedSuggestion);
                        setShowDropdown(false);
                        setSelectedSuggestionIndex(-1);
                        if (router) {
                            pushSearchClickEvent({
                                searchTerm: selectedSuggestion?.name || typedInput,
                                searchInput: typedInput,
                                suggestionPosition
                            });
                            const qs = new URLSearchParams({
                                searchterm: selectedSuggestion?.name || ''
                            }).toString();
                            router.push(`${SEARCH_CONSTANTS.pageUrl}?${qs}`);
                        }
                    }
                } else {
                    handleSearch();
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (showDropdown && suggestions.length > 0) {
                    setSelectedSuggestionIndex((prev)=>prev < suggestions.length - 1 ? prev + 1 : 0);
                } else if (!showDropdown && searchValue.trim()) {
                    // Show dropdown if not visible and has search value
                    setShowDropdown(true);
                    setSelectedSuggestionIndex(0);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (showDropdown && suggestions.length > 0) {
                    setSelectedSuggestionIndex((prev)=>prev > 0 ? prev - 1 : suggestions.length - 1);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setShowDropdown(false);
                setSelectedSuggestionIndex(-1);
                inputRef.current?.blur();
                break;
            case 'Tab':
                // Allow normal tab behavior but close dropdown
                if (showDropdown) {
                    setShowDropdown(false);
                    setSelectedSuggestionIndex(-1);
                }
                break;
            case 'Home':
                if (showDropdown && suggestions.length > 0) {
                    e.preventDefault();
                    setSelectedSuggestionIndex(0);
                }
                break;
            case 'End':
                if (showDropdown && suggestions.length > 0) {
                    e.preventDefault();
                    setSelectedSuggestionIndex(suggestions.length - 1);
                }
                break;
            default:
                // For any other key, reset selection
                if (showDropdown) {
                    setSelectedSuggestionIndex(-1);
                }
                break;
        }
    };
    const handleSuggestionClick = (suggestion, index, e)=>{
        // Prevent default to avoid any potential issues
        e?.preventDefault();
        e?.stopPropagation();
        console.log('suggestion', suggestion, suggestion.url, suggestion.name);
        // Immediately hide dropdown to prevent blur race condition
        setShowDropdown(false);
        setSelectedSuggestionIndex(-1);
        const typedInput = searchValue.trim();
        const suggestionPosition = index + 1;
        pushSearchClickEvent({
            searchTerm: suggestion.name || typedInput,
            searchInput: typedInput,
            suggestionPosition,
            results: suggestion?.url ? 'true' : ''
        });
        if (suggestion?.url && "object" !== 'undefined') {
            // Navigate to the suggestion's URL
            try {
                window.location.href = suggestion.url;
            } catch (error) {
                console.error('Error navigating to suggestion URL:', error);
                // Fallback to router navigation if window.location fails
                if (router) {
                    const qs = new URLSearchParams({
                        searchterm: suggestion.name || searchValue
                    }).toString();
                    router.push(`${SEARCH_CONSTANTS.pageUrl}?${qs}`);
                }
            }
        } else {
            // Fallback: redirect to search page with the selected suggestion
            setSearchValue(suggestion?.name || searchValue);
            onSuggestionSelect?.(suggestion);
            inputRef.current?.blur();
            if (router) {
                const qs = new URLSearchParams({
                    searchterm: suggestion?.name || searchValue
                }).toString();
                router.push(`${SEARCH_CONSTANTS.pageUrl}?${qs}`);
            }
        }
    };
    const handleFocus = ()=>{
        setIsFocused(true);
    };
    const handleBlur = (e)=>{
        // Check if the blur is caused by clicking on a suggestion
        // If the relatedTarget (the element receiving focus) is within the dropdown, don't hide it
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && dropdownRef.current?.contains(relatedTarget)) {
            return; // Don't hide dropdown if clicking inside it
        }
        // Check if the blur is caused by clicking the clear button
        if (relatedTarget && clearButtonRef.current?.contains(relatedTarget)) {
            return; // Don't hide dropdown if clicking the clear button
        }
        // If we're in the process of clearing, don't handle blur
        if (isClearingRef.current) {
            return;
        }
        // Delay hiding dropdown to allow for suggestion clicks
        setTimeout(()=>{
            // Double-check that we're still not focused on dropdown elements or clearing
            const activeElement = document.activeElement;
            if (!dropdownRef.current?.contains(activeElement) && !clearButtonRef.current?.contains(activeElement) && !isClearingRef.current) {
                setIsFocused(false);
                setShowDropdown(false);
                setSelectedSuggestionIndex(-1);
            }
        }, 150);
    };
    const { base, searchInputWrapper, inputWrapper, searchIcon, input, actionButtonWrapper, clearButton, searchButton, dropdown, suggestionHeader, suggestionItemsWrapper, suggestionItem, suggestionItemIcon, suggestionItemText } = TAILWIND_VARIANTS();
    const sourceId = useMemo(()=>process.env.NEXT_PUBLIC_SEARCH_GLOBAL_SOURCE_ID ? [
            process.env.NEXT_PUBLIC_SEARCH_GLOBAL_SOURCE_ID
        ] : undefined, []);
    useEffect(()=>{
        if (!isSearchInitialized) {
            return;
        }
        if (searchValue.trim() !== '' && searchValue.trim().length >= minCharactersForSuggestions) {
            const fetchData = async ()=>{
                const context = new Context({
                    locale: {
                        language: 'en',
                        country: 'us'
                    }
                });
                // Create a widget request with the entity "product" and widget ID "rfkid_7ss":
                const searchWidget = new SearchWidgetItem('content', 'rfkid_6', {
                    limit: 8,
                    offset: 0,
                    content: {},
                    facet: {
                        all: false
                    },
                    query: {
                        keyphrase: searchValue
                    },
                    suggestion: [
                        {
                            name: suggestionName,
                            max: 8,
                            keyphraseFallback: true
                        }
                    ]
                }, sourceId && sourceId.length > 0 ? sourceId : undefined);
                // Call the getWidgetData function with the widget request and the context to retrieve the data:
                const apiData = await getWidgetData(new WidgetRequestData([
                    searchWidget
                ]), context);
                const suggestionResultData = apiData?.widgets[0]?.content ?? [];
                setSuggestions(suggestionResultData);
            };
            fetchData();
        } else {
            // Clear suggestions when input is below minimum character requirement
            setSuggestions([]);
        }
    }, [
        searchValue,
        minCharactersForSuggestions,
        sourceId,
        suggestionName,
        isSearchInitialized
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: base(),
        ...getTestProps('global-search-input'),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: searchInputWrapper(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: inputWrapper(),
                        children: [
                            /*#__PURE__*/ _jsx(RemixIcon, {
                                name: "ri-search-line",
                                size: "md",
                                className: searchIcon()
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                id: id || 'global-search-input',
                                name: id || 'global-search-input',
                                ref: inputRef,
                                type: "text",
                                value: searchValue,
                                onChange: handleInputChange,
                                onKeyDown: handleKeyDown,
                                onFocus: handleFocus,
                                onBlur: handleBlur,
                                placeholder: placeholder,
                                disabled: disabled,
                                maxLength: maxLength,
                                className: input(),
                                role: "combobox",
                                "aria-expanded": showDropdown,
                                "aria-haspopup": "listbox",
                                "aria-autocomplete": "list",
                                "aria-controls": showDropdown ? 'search-suggestions' : undefined,
                                "aria-activedescendant": showDropdown && selectedSuggestionIndex >= 0 ? `suggestion-${suggestions[selectedSuggestionIndex]?.id || selectedSuggestionIndex}` : undefined,
                                "aria-owns": showDropdown ? 'search-suggestions' : undefined,
                                "aria-describedby": showDropdown ? 'suggestions-header' : undefined,
                                ...getTestProps('global-search-input-field')
                            })
                        ]
                    }),
                    searchValue && /*#__PURE__*/ _jsxs("div", {
                        className: actionButtonWrapper(),
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                ref: clearButtonRef,
                                type: "button",
                                onClick: handleClear,
                                className: clearButton(),
                                disabled: disabled,
                                "aria-label": "Clear search",
                                ...getTestProps('global-search-clear'),
                                children: /*#__PURE__*/ _jsx(RemixIcon, {
                                    name: "ri-close-line",
                                    size: "md"
                                })
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: handleSearch,
                                disabled: disabled || !searchValue.trim(),
                                className: searchButton(),
                                "aria-label": "Search",
                                ...getTestProps('global-search-submit'),
                                children: /*#__PURE__*/ _jsx(RemixIcon, {
                                    name: "ri-arrow-right-line",
                                    size: "md"
                                })
                            })
                        ]
                    })
                ]
            }),
            showDropdown && /*#__PURE__*/ _jsxs("div", {
                ref: dropdownRef,
                className: dropdown(),
                role: "listbox",
                id: "search-suggestions",
                "aria-label": `${suggestions.length} search suggestions`,
                "aria-live": "polite",
                ...getTestProps('global-search-suggestions-dropdown'),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: suggestionHeader(),
                        id: "suggestions-header",
                        children: [
                            "Suggestions (",
                            suggestions.length,
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: suggestionItemsWrapper(),
                        children: suggestions.map((suggestion, index)=>/*#__PURE__*/ _jsxs("button", {
                                id: `suggestion-${suggestion.id || index}`,
                                type: "button",
                                className: suggestionItem({
                                    isSelected: index === selectedSuggestionIndex
                                }),
                                onMouseDown: (e)=>{
                                    // Use onMouseDown to handle click before blur event fires
                                    e.preventDefault(); // Prevent input blur
                                    handleSuggestionClick(suggestion, index, e);
                                },
                                role: "option",
                                "aria-selected": index === selectedSuggestionIndex,
                                "aria-describedby": "suggestions-header",
                                tabIndex: -1,
                                "aria-label": suggestion.name,
                                ...getTestProps(`global-search-suggestion-${suggestion.id || index}`),
                                children: [
                                    /*#__PURE__*/ _jsx(RemixIcon, {
                                        name: "ri-search-line",
                                        size: "md",
                                        className: suggestionItemIcon({
                                            isSelected: index === selectedSuggestionIndex
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: suggestionItemText({
                                            isSelected: index === selectedSuggestionIndex
                                        }),
                                        children: suggestion.name
                                    })
                                ]
                            }, suggestion.id || `suggestion-${index}`))
                    })
                ]
            })
        ]
    });
};
export default GlobalSearchInput;
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'w-full',
            'relative',
            'z-[10]'
        ],
        searchInputWrapper: [
            'w-full',
            'w-max-w-[627px]',
            'h-12',
            'py-component-tab-layout-container-padding-x',
            'px-spacing-spacing-8',
            'flex',
            'flex-row',
            'justify-between',
            'gap-spacing-spacing-8',
            'bg-color-grayscale-white',
            'border',
            'border-p-color-border',
            'rounded-border-radius-radius-2',
            'relative'
        ],
        inputWrapper: [
            'w-full',
            'h-full',
            'flex',
            'flex-row',
            'flex-start',
            'py-spacing-spacing-12',
            'gap-spacing-spacing-8'
        ],
        searchIcon: [
            'text-color-grayscale-black'
        ],
        input: [
            'w-full',
            'h-full',
            'flex-1',
            'bg-color-grayscale-white',
            'border-none',
            'outline-none',
            'font-typography-font-family-body',
            'text-p-color-text',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'font-normal',
            'placeholder:text-p-color-text-secondary',
            'placeholder:font-typography-font-family-body',
            'placeholder:text-typography-font-size-body-medium',
            'placeholder:leading-typography-line-height-body-medium',
            'placeholder:italic'
        ],
        actionButtonWrapper: [
            'w-fit',
            'flex',
            'flex-row',
            'items-center',
            'justify-between',
            'gap-spacing-spacing-4',
            'py-spacing-spacing-8'
        ],
        clearButton: [
            'flex',
            'items-center',
            'justify-center',
            'w-8',
            'h-8',
            'p-spacing-spacing-12',
            'bg-component-button-secondary-bg',
            'rounded-general-border-radius-button',
            'border',
            'border-component-button-secondary-border',
            'border-general-border-width-button',
            'border-component-button-secondary-border',
            'text-component-button-secondary-icon'
        ],
        searchButton: [
            'flex',
            'items-center',
            'justify-center',
            'w-8',
            'h-8',
            'p-spacing-spacing-12',
            'rounded-general-border-radius-button',
            'bg-component-button-primary-bg',
            'text-component-button-primary-text'
        ],
        dropdown: [
            'absolute',
            'top-full',
            'left-0',
            'right-0',
            'mt-2',
            'flex',
            'flex-col',
            'gap-padding-less',
            'p-padding-less',
            'bg-color-grayscale-white',
            'border',
            'border-p-color-border-hover',
            'rounded-[16px]',
            'max-h-[380px]',
            'overflow-y-auto'
        ],
        suggestionHeader: [
            'w-full',
            'text-left',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'text-color-text-text',
            'font-bold',
            'leading-typography-line-height-body-medium'
        ],
        suggestionItemsWrapper: [
            'w-full',
            'flex',
            'flex-col',
            'gap-padding-micro'
        ],
        suggestionItem: [
            'w-full',
            'h-full',
            'flex',
            'flex-row',
            'items-center',
            'gap-spacing-spacing-8',
            'justify-start',
            'p-spacing-spacing-2',
            'bg-color-grayscale-white',
            'rounded-general-border-radius-none',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'text-color-text-text',
            'hover:bg-color-core-color-4-skyblue-lightTint',
            'hover:text-color-core-color-4-skyblue-darkTint',
            'focus:bg-color-core-color-4-skyblue-lightTint',
            'focus:text-color-core-color-4-skyblue-darkTint',
            'focus:outline-border-p-color-border-focus',
            'transition-colors',
            'duration-150'
        ],
        suggestionItemIcon: [
            'w-6',
            'h-6',
            'text-component-button-secondary-icon'
        ],
        suggestionItemText: [
            'text-left',
            'text-component-button-tertiary-text',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-typography-line-height-xxsmall'
        ]
    },
    variants: {
        isSelected: {
            true: {
                suggestionItem: [
                    'bg-color-core-color-4-skyblue-lightTint',
                    'text-color-core-color-4-skyblue-darkTint',
                    'font-semibold',
                    'ring-2',
                    'ring-color-core-color-4-skyblue-lightTint',
                    'ring-offset-1'
                ],
                suggestionItemIcon: [
                    'text-color-core-color-4-skyblue-darkTint'
                ],
                suggestionItemText: [
                    'text-color-core-color-4-skyblue-darkTint',
                    'font-semibold'
                ]
            },
            false: {
                suggestionItem: [
                    'bg-color-grayscale-white'
                ],
                suggestionItemIcon: [
                    'text-component-button-secondary-icon'
                ],
                suggestionItemText: [
                    'text-component-button-tertiary-text'
                ]
            }
        }
    }
});
